import React from 'react';
import { ChevronDown } from 'lucide-react';
import * as Accordion from '@radix-ui/react-accordion';

export const DEFAULT_FAQS = [
    {
      question: 'How much does it cost to consult Manage Your Loans?',
      answer: 'There is no cost to you (the borrower) when taking on the services of an MYL broker. MYL typically earn a commission or fee from lenders for each successfully settled loan. You are under no obligation to take a loan after reviewing options nor is there any cost involved for our customers.',
    },
    {
      question: 'How long does the loan approval process take?',
      answer: 'It depends on the type of loan. For instance, home and car loans typically require collateral, while personal loans may be unsecured. Our loan representatives can provide specific information based on your chosen loan type. Generally, pre-approval can take 24-48 hours, with final approval taking 1-2 weeks depending on your documentation and lender requirements.',
    },
    {
      question: 'What documents do I need to provide to the broker?',
      answer: 'Typically, you will need: Proof of Identity (driver\'s license or passport), Proof of Income (recent pay slips, tax returns), Proof of Savings (bank statements), Details of Current Debts (credit card statements, loan documents), Proof of Living Expenses, Credit History Report, Details of Assets, and for Self-Employed Applicants: business financial statements and GST returns. We\'ll guide you through the exact requirements based on your situation.',
    },
    {
      question: 'How is my personal information protected with a broker?',
      answer: 'At Manage Your Loans, your data protection is our utmost priority. We use advanced encryption techniques to safeguard all information shared electronically. Only authorized personnel have controlled access to your data. We retain personal data only as long as necessary for our services and legal requirements, after which it is securely destroyed or anonymized. We only work with partners who maintain the same stringent data protection standards.',
    },
    {
      question: 'Are the interest rates offered through a broker competitive?',
      answer: 'Our brokers have access to a broad range of lenders and loan products, from major banks to smaller credit institutions. This allows us to find competitive rates tailored to your specific needs. With our industry knowledge and relationships, we can often negotiate a sharper rate on your behalf, although no particular rate is guaranteed. We provide clear rate breakdowns so you can compare offerings and make informed decisions.',
    },
    {
      question: 'What makes Manage Your Loans different from other brokers?',
      answer: 'We compare home loans from a panel of 80+ lenders, have helped clients with more than $500M in loans, and have been recognised in Australian Broker\'s 5-Star Mortgage Innovators list and as a finalist in several national broking awards. Our team combines technology with personalised service across our Darwin, Melbourne, Brisbane and Sydney offices.',
    },
    {
      question: 'Can you help with investment property loans?',
      answer: 'Yes, we assist with investment property loans and can help you explore loan structures suited to your goals. We recommend working with your financial adviser and accountant alongside our brokers to ensure your lending aligns with your broader financial plan.',
    },
    {
      question: 'Do you offer services throughout Australia?',
      answer: 'Yes, while we are based in Darwin, NT, we provide loan brokerage services across all Australian states and territories. We work with clients nationwide through phone, video calls, and our secure online platform, making it easy to access our award-winning services no matter where you are located.',
    },
];

const FAQ = ({
  faqs = DEFAULT_FAQS,
  eyebrow = 'FAQ',
  title = 'Frequently Asked Questions',
  subtitle = 'Find answers to common questions about our services',
}) => {
  return (
    <section id="faq" className="py-20 bg-white scroll-mt-20" data-testid="faq-section">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#99DF63]/10 px-6 py-2 rounded-full mb-6">
            <ChevronDown className="w-5 h-5 text-[#00594C]" />
            <span className="text-[#00594C] font-semibold">{eyebrow}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#00594C] mb-4" data-testid="faq-title">
            {title}
          </h2>
          <p className="text-xl text-gray-600">
            {subtitle}
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion.Root type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <Accordion.Item
              key={index}
              value={`item-${index}`}
              className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-[#99DF63] transition-colors"
              data-testid={`faq-item-${index}`}
            >
              <Accordion.Header>
                <Accordion.Trigger className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors group">
                  <span className="text-lg font-semibold text-[#00594C] pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown className="w-5 h-5 text-[#00594C] transition-transform group-data-[state=open]:rotate-180 flex-shrink-0" />
                </Accordion.Trigger>
              </Accordion.Header>
              {/* forceMount keeps every answer in the prerendered HTML, so
                  non-JS fetchers (ChatGPT-User, Claude-User, PerplexityBot)
                  can read it. Closed items are hidden with CSS only. */}
              <Accordion.Content forceMount className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:hidden">
                <div className="p-6 pt-0 text-gray-600 leading-relaxed">
                  {faq.answer}
                </div>
              </Accordion.Content>
            </Accordion.Item>
          ))}
        </Accordion.Root>

        {/* Contact CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-[#00594C] to-[#003d35] rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
          <p className="mb-6 text-gray-200">
            Our team is here to help. Get in touch for personalized answers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+61468007056"
              className="inline-flex items-center justify-center bg-white text-[#00594C] px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all"
              data-testid="faq-contact-phone"
            >
              Call 0468 007 056
            </a>
            <a
              href="#book-appointment"
              className="inline-flex items-center justify-center bg-[#99DF63] text-[#00594C] px-6 py-3 rounded-full font-semibold hover:bg-[#8BCF52] transition-all"
              data-testid="faq-contact-book"
            >
              Book a Consultation
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;